import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const letters = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"];

const CircleCorrectNumber: React.FC = () => {
    const [targetLetter, setTargetLetter] = useState("");
    const [letterGrid, setLetterGrid] = useState<string[]>([]);
    const [selectedLetters, setSelectedLetters] = useState<number[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Circle the Correct Number – Fun Counting Practice for Nursery Kids"; // Set page title
    }, []);

    const shuffleArray = (array: string[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const generateNewQuestion = () => {
        const newTarget = letters[Math.floor(Math.random() * letters.length)];
        setTargetLetter(newTarget);

        const options = Array.from({ length: 14 }, () => letters[Math.floor(Math.random() * letters.length)]);

        // Ensure correct letter appears at least twice
        options.push(newTarget, newTarget);

        setLetterGrid(shuffleArray(options));
        setSelectedLetters([]);
        setMessage("");
    };

    const handleLetterClick = (index: number) => {
        let newSelectedLetters = [...selectedLetters];
        if (newSelectedLetters.includes(index)) {
            newSelectedLetters = newSelectedLetters.filter(i => i !== index);
        } else {
            newSelectedLetters.push(index);
        }
        setSelectedLetters(newSelectedLetters);

        // Check if all correct letters are selected
        const correctSelections = letterGrid.reduce((acc, l, i) => l === targetLetter ? [...acc, i] : acc, [] as number[]);
        if (newSelectedLetters.length === correctSelections.length && newSelectedLetters.every(i => correctSelections.includes(i))) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
                setMessage('')
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setMessage("");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Enhance number recognition skills with the "Circle the Correct Number" activity on Mamas Guidance — an engaging math exercise for nursery kids to identify and select the correct number based on visual prompts.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord matchImage  match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Circle the Correct Number</h1>

                                <p>
                                    Want to make counting fun for your preschooler? Our <strong>Circle the Correct Number worksheet</strong> is designed to help young children practice <strong>identifying numbers</strong>. It’s perfect for nursery.
                                </p>

                                <h2>🔍 What Is This Worksheet About?</h2>
                                <p>
                                    In this activity, children will see pictures of common items — such as apples, balls, stars, or animals — and choose the correct number that represents the quantity shown. They simply need to <strong>count and circle the correct number</strong>.
                                </p>

                                <h2>📚 Related Worksheets</h2>
                                <div>
                                    <IonItem routerLink="/nursery/mathematics/count-object"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Count Object</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Numbers</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Worksheet Works</h2>
                                <p>
                                    Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Circle the Correct Number</h1>

                            <p>
                                Want to make counting fun for your preschooler? Our <strong>Circle the Correct Number worksheet</strong> is designed to help young children practice <strong>identifying numbers</strong>. It’s perfect for nursery.
                            </p>

                            <h2>🔍 What Is This Worksheet About?</h2>
                            <p>
                                In this activity, children will see pictures of common items — such as apples, balls, stars, or animals — and choose the correct number that represents the quantity shown. They simply need to <strong>count and circle the correct number</strong>.
                            </p>

                            <h2>📚 Related Worksheets</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/before-after-between-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Before / After / Between Numbers</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Worksheet Works</h2>
                            <p>
                                Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                            </p>
                        </article>
                        <h3>Circle all the <span style={{ fontSize: '30px' }}>{targetLetter}</span> number</h3>
                        <div
                            className="mb-4 letter-grid"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(4, 50px)",
                                gap: "10px",
                                justifyContent: "center",
                            }}
                        >
                            {letterGrid.map((letter, index) => (
                                <div
                                    key={index}
                                    className={`letter-box ${selectedLetters.includes(index) ? (letter === targetLetter ? "correct" : "wrong") : ""}`}
                                    onClick={() => handleLetterClick(index)}
                                    style={{
                                        width: "50px",
                                        height: "50px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: "2px solid #000",
                                        borderRadius: "50%",
                                        fontSize: "20px",
                                        cursor: "pointer",
                                        backgroundColor: selectedLetters.includes(index)
                                            ? letter === targetLetter
                                                ? "lightgreen"
                                                : "lightcoral"
                                            : "white",
                                    }}
                                >
                                    {letter}
                                </div>
                            ))}
                        </div>
                        {message && <h4 style={{ marginTop: "20px", color: "green" }}>{message}</h4>}
                        <div className="btn hero-btn bigPurpleButton" onClick={generateNewQuestion} style={{ marginTop: "20px" }}>Next Question</div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default CircleCorrectNumber;
